// src/features/groups/components/GroupActions.tsx

import React, { useState } from 'react';
import {
  useNotify,
  useRefresh,
  useDataProvider,
  useTranslate,
  useRedirect,
} from 'react-admin';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
  Visibility,
} from '@mui/icons-material';
import { useGroupPermissions } from '../hooks/useGroupPermissions';
import type { Group } from '../types';

interface GroupActionsProps {
  record: Group;
}

/**
 * Row actions for Groups list
 * Shows menu with Edit, View, and Delete options based on permissions
 */
const GroupActions: React.FC<GroupActionsProps> = ({ record }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const { canEdit, canDelete, canShow } = useGroupPermissions();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const open = Boolean(anchorEl);

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  // Action handlers
  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    redirect('edit', 'groups', record.id);
  };

  const handleShow = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    redirect('show', 'groups', record.id);
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);

    try {
      await dataProvider.delete('groups', {
        id: record.id,
        previousData: record,
      });

      notify('resources.groups.messages.delete_success', { type: 'success' });
      handleDeleteDialogClose();
      refresh();
    } catch (error: any) {
      const errorMessage = error?.body?.error?.message || 
                          error?.message || 
                          translate('resources.groups.messages.delete_error');
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  // Check if group has hotels
  const hasHotels = record.hotels && record.hotels.length > 0;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={translate('ra.action.menu')}>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ color: 'action.active' }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu
        id="group-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={(e: any) => handleClose(e)}
        onClick={(e) => e.stopPropagation()}
        MenuListProps={{
          'aria-labelledby': 'group-actions-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
      
        {/* Edit */}
        {canEdit && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('ra.action.edit')}</ListItemText>
          </MenuItem>
        )}

        {/* Delete */}
        {canDelete && (
          <MenuItem
            onClick={handleDeleteClick}
            disabled={hasHotels}
            sx={{
              color: hasHotels ? 'text.disabled' : 'error.main',
              '&:hover': {
                backgroundColor: hasHotels ? 'transparent' : 'error.light',
              },
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color={hasHotels ? 'disabled' : 'error'} />
            </ListItemIcon>
            <ListItemText>
              {translate('ra.action.delete')}
              {hasHotels && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {translate('resources.groups.messages.delete_with_hotels')}
                </Typography>
              )}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>
          {translate('resources.groups.messages.delete_confirm')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translate('ra.message.delete_content', {
              name: record.name,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={isDeleting}>
            {translate('ra.action.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            autoFocus
          >
            {isDeleting 
              ? translate('ra.action.loading') 
              : translate('ra.action.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GroupActions;